import React, { useMemo } from "react";
import { Box, Typography, Paper, CircularProgress, useTheme } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import {
  DataGrid,
  type GridCellParams,
  type GridColDef,
  type GridRowsProp,
} from "@mui/x-data-grid";
import type {
  ScorecardBrandMapping,
  ScorecardConfig,
  ScorecardDatasetMetricRow,
  ScorecardQuestionRow,
  ScorecardRollingAverageConfig,
  ScorecardRow,
  ScorecardTimePeriod,
  ScorecardTimePeriodSelection,
} from "@models/Scorecard";
import { isDatasetMetricRow, isQuestionRow, isHeadingRow, normalizeScorecardRow } from "@models/Scorecard";
import type { GuardrailRollingOption } from "@models/Dataset";
import { useScorecardQuestionRowData } from "@components/charts/scorecard-config/hooks/useScorecardQuestionRowData";
import { useScorecardDatasetMetricRowData } from "@components/charts/scorecard-config/hooks/useScorecardDatasetMetricRowData";
import { useScorecardColumnWidths } from "@hooks/use-scorecard-column-widths";
import { useAppSelector } from "@store/index";
import { dataAvailabilityPreviewSelector } from "@store/slices/data-availability-preview-slice";
import HPTooltip from "@components/common/Tooltip";
import { resolveTimePeriodDates } from "@utils/time-period-utils";
import { getBaseMappingForBrand } from "@utils/scorecard-fetch-utils";
import { getVisibleOrderedBrands } from "@utils/scorecard-column-utils";
import { getScorecardGridSx } from "./ScorecardChart.styles";

/** Everything that can change a metric row's displayed values without
 *  changing id/label/type: its own brand mappings, its Base config, and —
 *  when its Base is an Existing Metric Row — that referenced row's own
 *  brand mappings too (the diagonal lookup depends on both). */
function getRowMappingSignature(row: ScorecardQuestionRow, allRows: ScorecardRow[]): string {
  const rowBase = row.rowBase;
  const baseRow =
    rowBase?.kind === "existing_question_row"
      ? allRows.find((r) => r.id === rowBase.questionRowId)
      : undefined;
  return JSON.stringify({
    brandMappings: row.brandMappings,
    rowBase: row.rowBase,
    baseRowMappings: baseRow && isQuestionRow(baseRow) ? baseRow.brandMappings : undefined,
  });
}

/** Metric row equivalent of getRowMappingSignature — simpler, since a Metric
 *  row's Base is the referenced metric's own saved Base, never another row. */
function getDatasetMetricRowMappingSignature(row: ScorecardDatasetMetricRow): string {
  return JSON.stringify({ brandMappings: row.brandMappings, base: row.base });
}

interface ScorecardChartProps {
  config: ScorecardConfig;
  datasetId?: string;
  viewerState?: {
    rollingAverage: number | null;
    timeUnit: string | null;
    timePeriod: string | null;
  };
  dataAvailabilityDate?: string | null;
  dashboardId?: string;
  earliestResponseDate?: string | null;
}

/**
 * Renders the Scorecard as a Metric x Brand table, one row per Metric/
 * Heading Row and one column per visible brand in its configured display
 * order (Primary included — it can sit anywhere, not just first). Styled to
 * match CrosstabTable's design language (DataGrid, bold header, label-column
 * border, footer summary bar) even though there is no cross/pivot dimension
 * here — just a flat table. Each metric row fetches its own data via the
 * existing bar-chart-family search endpoint (no dedicated Scorecard endpoint
 * exists yet) and reconciles the response to brand columns by normalized label.
 */
const ScorecardChart: React.FC<ScorecardChartProps> = ({
  config,
  datasetId,
  viewerState,
  dataAvailabilityDate,
  dashboardId,
  earliestResponseDate,
}) => {
  // When a preview applies to this dashboard+dataset, dataAvailabilityDate IS the
  // preview cutoff (substituted upstream in ChartWidget). Build the all_time range
  // [earliest → cutoff] ONLY while preview is active, so normal (non-preview)
  // all_time scorecards keep their existing no-date behavior. Both dates are sent
  // together so the range is never half-open.
  const preview = useAppSelector(dataAvailabilityPreviewSelector);
  // Memoized so its identity is stable across renders (it feeds the row-data hooks'
  // effect deps); a fresh object each render would run those effects every render.
  const previewActiveForWidget =
    preview.isActive &&
    preview.dashboardId === dashboardId &&
    preview.datasetId === datasetId;
  const allTimePreviewRange = useMemo(
    () =>
      previewActiveForWidget && dataAvailabilityDate
        ? {
            startDate: earliestResponseDate ?? "1970-01-01",
            endDate: dataAvailabilityDate,
          }
        : null,
    [previewActiveForWidget, dataAvailabilityDate, earliestResponseDate],
  );
  const { brands, weight, timePeriodSelection, rollingAverage, percentageFormatting, meanFormatting } = config;
  const theme = useTheme();
  const primaryColor = config.primaryBrandHighlightColor || theme.palette.primary.main;
  // Dashboard widgets render straight from persisted content (see the rows
  // normalization above), so configs saved before Column Customization
  // existed won't have these fields — default defensively rather than
  // relying on loadConfig's normalization, which only runs in the editor.
  const orderedBrands = useMemo(
    () => getVisibleOrderedBrands(brands, config.columnOrder ?? [], config.hiddenBrandIds ?? []),
    [brands, config.columnOrder, config.hiddenBrandIds],
  );
  // Dashboard widgets render straight from persisted content (bypassing the
  // editor's loadConfig reducer, where this normalization otherwise happens),
  // so rows saved before the Question/Metric rename — still tagged
  // type: "metric" for what's now a Question row — must be migrated here too.
  // Idempotent: already-current rows pass through unchanged.
  const rows = useMemo(
    () => (config.rows ?? []).map(normalizeScorecardRow),
    [config.rows],
  );
  const metricColumnHeader = config.metricColumnHeader?.trim() || "Metric";
  // Heading rows render full-width (colSpan) and never show metric-row
  // content, so their label never factors into the Metric column's width.
  const nonHeadingRowLabels = useMemo(
    () => rows.filter((row) => !isHeadingRow(row)).map((row) => row.label),
    [rows],
  );
  const { containerRef, labelColumnWidth, brandColumnWidths } = useScorecardColumnWidths({
    metricColumnHeader,
    rowLabels: nonHeadingRowLabels,
    brands: orderedBrands,
  });
  // Memoized so each hook receives a stable array identity (these feed the
  // row-data hooks' effect deps).
  const questionRows = useMemo(() => rows.filter(isQuestionRow), [rows]);
  const datasetMetricRows = useMemo(
    () => rows.filter(isDatasetMetricRow),
    [rows],
  );

  const viewerTimePeriod = viewerState?.timePeriod ?? null;
  const effectiveTimePeriodSelection: ScorecardTimePeriodSelection = useMemo(() => {
    if (!viewerTimePeriod || viewerTimePeriod === timePeriodSelection.timePeriod) {
      return timePeriodSelection;
    }
    return resolveTimePeriodDates(
      {
        timePeriod: viewerTimePeriod as ScorecardTimePeriod,
        startDate: null,
        endDate: null,
      },
      dataAvailabilityDate ?? null,
    );
  }, [viewerTimePeriod, timePeriodSelection, dataAvailabilityDate]);

  const viewerRollingAverage = viewerState?.rollingAverage ?? null;
  const effectiveRollingAverage: ScorecardRollingAverageConfig = useMemo(() => {
    if (viewerRollingAverage === 0) {
      return { enabled: false, windowSize: rollingAverage.windowSize };
    }
    if (viewerRollingAverage !== null) {
      return {
        enabled: true,
        windowSize: viewerRollingAverage as GuardrailRollingOption,
      };
    }
    return rollingAverage;
  }, [viewerRollingAverage, rollingAverage]);

  const { getRowCells: getQuestionRowCells, isRowLoading: isQuestionRowLoading } =
    useScorecardQuestionRowData(
      datasetId ?? null,
      questionRows,
      weight,
      effectiveTimePeriodSelection,
      effectiveRollingAverage,
      allTimePreviewRange,
    );
  const { getRowCells: getDatasetMetricRowCells, isRowLoading: isDatasetMetricRowLoading } =
    useScorecardDatasetMetricRowData(
      datasetId ?? null,
      datasetMetricRows,
      weight,
      effectiveTimePeriodSelection,
      effectiveRollingAverage,
      allTimePreviewRange,
    );

  const renderMetricCell = (
    row: ScorecardQuestionRow | ScorecardDatasetMetricRow,
    brandId: string,
    status: ScorecardBrandMapping["status"] | undefined,
    baseStatus: ScorecardBrandMapping["status"] | undefined,
  ) => {
    if (status === "unavailable" || baseStatus === "unavailable") {
      return <Typography variant="caption" color="text.secondary">N/A</Typography>;
    }
    if (status !== "matched" && status !== "manual") {
      return (
        <Typography variant="caption" color="warning.main">
          Unresolved
        </Typography>
      );
    }
    if (baseStatus !== undefined && baseStatus !== "matched" && baseStatus !== "manual") {
      return (
        <Typography variant="caption" color="warning.main">
          Base Unresolved
        </Typography>
      );
    }
    const isLoading = isQuestionRow(row) ? isQuestionRowLoading(row) : isDatasetMetricRowLoading(row);
    if (isLoading) {
      return <CircularProgress size={14} />;
    }
    const cell = isQuestionRow(row)
      ? getQuestionRowCells(row)[brandId]
      : getDatasetMetricRowCells(row)[brandId];
    if (!cell || cell.value === null) {
      return <Typography variant="caption" color="text.secondary">—</Typography>;
    }
    if (cell.suppressed) {
      return <Typography variant="caption" color="text.secondary">Suppressed</Typography>;
    }
    const rounding = row.resultType === "mean" ? meanFormatting : percentageFormatting;
    const suffix = row.resultType === "mean" ? "" : "%";
    return `${cell.value.toFixed(rounding)}${suffix}`;
  };

  const totalColumns = orderedBrands.length + 1;

  const labelColumn: GridColDef = useMemo(
    () => ({
      field: "label",
      headerName: metricColumnHeader,
      width: labelColumnWidth,
      headerAlign: "left",
      align: "left",
      sortable: false,
      disableColumnMenu: true,
      resizable: false,
      cellClassName: (params) =>
        params.row?.__type === "heading" ? "scorecard-heading-cell" : "scorecard-label-cell",
      colSpan: (params) => (params.row?.__type === "heading" ? totalColumns : undefined),
      renderCell: (params) => {
        const text = params.value as string;
        return (
          <HPTooltip title={text} placement="top" arrow>
            <Box
              sx={{
                fontWeight: 700,
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {text}
            </Box>
          </HPTooltip>
        );
      },
    }),
    [totalColumns, metricColumnHeader, labelColumnWidth],
  );

  const brandColumns: GridColDef[] = useMemo(
    () =>
      orderedBrands.map((brand, index) => {
        const isLastColumn = index === orderedBrands.length - 1 && brand.role !== "primary";
        return {
          field: brand.id,
          headerName: brand.label,
          width: brandColumnWidths[brand.id],
          headerAlign: "center",
          align: "center",
          sortable: false,
          disableColumnMenu: true,
          resizable: false,
          headerClassName:
            brand.role === "primary"
              ? `scorecard-primary-column scorecard-primary-column-top${
                  rows.length === 0 ? " scorecard-primary-column-bottom" : ""
                }`
              : isLastColumn
                ? "scorecard-last-column"
                : undefined,
          cellClassName:
            brand.role === "primary"
              ? (params: GridCellParams) =>
                  params.row?.id === rows[rows.length - 1]?.id
                    ? "scorecard-primary-column scorecard-primary-column-bottom"
                    : "scorecard-primary-column"
              : isLastColumn
                ? "scorecard-last-column"
                : undefined,
          renderHeader: () => (
            <Box sx={{ textAlign: "center" }}>
              <Box
                className={brand.role === "primary" ? "scorecard-primary-header-text" : undefined}
                sx={{ fontWeight: 700 }}
              >
                {brand.label}
                {brand.role === "primary" && (
                  <Box component="span" sx={visuallyHidden}>
                    {" "}
                    (Primary Brand)
                  </Box>
                )}
              </Box>
            </Box>
          ),
          renderCell: (params: GridCellParams) => {
            const row: ScorecardRow | undefined = rows.find((r) => r.id === params.row?.id);
            if (!row) return null;
            if (isQuestionRow(row)) {
              const mapping = row.brandMappings.find((m) => m.brandId === brand.id);
              const baseMapping = row.rowBase
                ? getBaseMappingForBrand(row, brand.id, questionRows)
                : undefined;
              return renderMetricCell(row, brand.id, mapping?.status, baseMapping?.status);
            }
            if (isDatasetMetricRow(row)) {
              const mapping = row.brandMappings.find((m) => m.brandId === brand.id);
              return renderMetricCell(row, brand.id, mapping?.status, undefined);
            }
            return null;
          },
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      orderedBrands,
      brandColumnWidths,
      rows,
      isQuestionRowLoading,
      isDatasetMetricRowLoading,
      meanFormatting,
      percentageFormatting,
    ],
  );

  const columns: GridColDef[] = [labelColumn, ...brandColumns];

  const dataRows: GridRowsProp = rows.map((row) => ({
    id: row.id,
    label: row.label,
    __type: row.type,
    // DataGrid decides whether to re-render a row's cells based on this row
    // object — brandMappings/rowBase edits (this row's own, or a
    // referenced row's, when this row's Base is an Existing Question Row)
    // never touch id/label/__type, so without this DataGrid can skip
    // re-invoking renderCell and keep showing stale diagonal/flat values
    // even though getRowCells would compute a different answer if called.
    __mappingSignature: isQuestionRow(row)
      ? getRowMappingSignature(row, rows)
      : isDatasetMetricRow(row)
        ? getDatasetMetricRowMappingSignature(row)
        : "",
  }));

  if (brands.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Select brands to see a preview of the Scorecard.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box ref={containerRef} sx={{ width: "100%", flex: 1, minHeight: 0 }}>
        <DataGrid
          rows={dataRows}
          columns={columns}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnResize
          hideFooter
          columnHeaderHeight={56}
          rowHeight={52}
          localeText={{ noRowsLabel: "This Scorecard has no rows yet." }}
          getRowClassName={(params) =>
            params.row?.__type === "heading" ? "scorecard-heading-row" : ""
          }
          sx={getScorecardGridSx(primaryColor)}
        />
      </Box>

      <Box
        sx={{
          mt: 1,
          p: 1,
          px: 2.5,
          backgroundColor: "#f9fafb",
          borderRadius: 1,
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          Weight: {weight === "DISABLED" ? "No Weight" : "Default Weight"}
        </Typography>
      </Box>
    </Paper>
  );
};

export default ScorecardChart;
